import { useState, useEffect, useRef } from 'react';
import DAWS from '../constants/daws';
import { useAuth } from '../hooks/useAuth';
import useLang from '../hooks/useLang';
import { supabase } from '../lib/supabase';
import { confirmDialog } from '../lib/confirm.jsx';
import API from '../constants/api';
import { loadArchipelSubscribed, setArchipelSubscribed } from '../lib/archipelConsent';

// Adresse de contact unique (cf. pages légales). Centralisée ici pour
// pouvoir construire les mailto de résiliation et suppression de compte
// sans duppliquer la chaîne.
const CONTACT_EMAIL = 'contact@versions.studio';

// Construit un lien mailto encodé. Subject + body peuvent contenir
// du texte multi-lignes (les \n dans body sont encodés).
function buildMailto(subject, body) {
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  if (body) params.set('body', body);
  // URLSearchParams encode les espaces en "+" — pour un mailto on
  // préfère %20 (certains clients mail interprètent mal le "+"). On
  // remplace après coup pour garder l'encodage des autres caractères.
  const qs = params.toString().replace(/\+/g, '%20');
  return `mailto:${CONTACT_EMAIL}?${qs}`;
}

/* ═══════════════════════════════════════════════════════════ */
/* RÉGLAGES — mini-modal v2                                    */
/* Style aligné sur la maquette _refonte-fiche-ref :           */
/* head mono · titre avec un mot en amber italique · rangées  */
/* .rg-row compactes (label + hint à gauche, widget à droite) */
/* + footer Se déconnecter / Enregistrer.                      */
/* ═══════════════════════════════════════════════════════════ */
export default function ReglagesScreen({ onSignOut, onGoHome, onProfileUpdate, onClose }) {
  const { user } = useAuth();
  const { s, lang, setLang } = useLang();
  const fileRef = useRef(null);

  // ── Profile state ──
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [defaultDaw, setDefaultDaw] = useState('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // ── Crédits / abonnement ──
  // Mirror du flow App.jsx (rail bas-gauche). On refetch au montage de
  // la modale pour avoir les chiffres frais — pas de prop drilling.
  // Schema (cf. RPC get_or_create_user_credits, migration 020) :
  //   balance_remaining   : total restant (sub + pack)
  //   subscription_balance: crédits du mois en cours (purgés à résiliation)
  //   pack_balance        : crédits packs (cumulables, jamais purgés)
  //   monthly_grant       : taille du grant mensuel (>0 = abonné)
  //   monthly_renews_at   : date du prochain renew
  const [creditsRow, setCreditsRow] = useState(null);
  useEffect(() => {
    if (!user?.id) { setCreditsRow(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_or_create_user_credits');
        if (cancelled) return;
        if (error) {
          console.warn('[reglages] get_or_create_user_credits failed:', error.message);
          return;
        }
        const row = Array.isArray(data) ? data[0] : data;
        setCreditsRow(row || null);
      } catch (e) {
        console.warn('[reglages] credits fetch threw:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // ── Nouvelles d'Archipel Audio (migration 052) ──
  // null = pas encore chargé (ou table absente) → la rangée n'est pas
  // affichée. Sans choix antérieur (comptes créés avant la mention),
  // c'est « Non » : passer à « Oui » est un opt-in explicite.
  const [archipelOn, setArchipelOn] = useState(null);
  const [archipelBusy, setArchipelBusy] = useState(false);
  const [archipelErr, setArchipelErr] = useState(false);
  useEffect(() => {
    if (!user?.id) { setArchipelOn(null); return; }
    let cancelled = false;
    loadArchipelSubscribed(user.id)
      .then((on) => { if (!cancelled) setArchipelOn(on); })
      .catch((e) => console.warn('[reglages] archipel consent load failed:', e && e.message));
    return () => { cancelled = true; };
  }, [user?.id]);

  const handleArchipel = async (want) => {
    if (archipelBusy || archipelOn === null || archipelOn === want) return;
    const prev = archipelOn;
    setArchipelOn(want);
    setArchipelErr(false);
    setArchipelBusy(true);
    try {
      await setArchipelSubscribed(want, lang);
    } catch (e) {
      console.warn('[reglages] archipel consent save failed:', e && e.message);
      setArchipelOn(prev);
      setArchipelErr(true);
    } finally {
      setArchipelBusy(false);
    }
  };

  // ── Load profile on mount ──
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('prenom, nom, avatar_url, default_daw, langue, iban, bic')
          .eq('id', user.id)
          .single();
        if (data) {
          setPrenom(data.prenom || '');
          setNom(data.nom || '');
          setAvatarUrl(data.avatar_url || null);
          setDefaultDaw(data.default_daw || '');
          setIban(data.iban || '');
          setBic(data.bic || '');
        }
      } catch (e) {
        console.warn('load profile:', e);
      } finally {
        setLoadingProfile(false);
      }
    };
    load();
  }, [user]);

  // ── Save profile ──
  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setSaved(false);
    try {
      await supabase.from('profiles').upsert({
        id: user.id,
        prenom: prenom.trim(),
        nom: nom.trim(),
        avatar_url: avatarUrl,
        default_daw: defaultDaw,
        iban: iban.trim(),
        bic: bic.trim(),
        updated_at: new Date().toISOString(),
      });
      setSaved(true);
      if (onProfileUpdate) onProfileUpdate({ prenom: prenom.trim(), nom: nom.trim(), avatar_url: avatarUrl, default_daw: defaultDaw, langue: lang });
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.warn('save profile:', e);
    } finally {
      setSaving(false);
    }
  };

  // ── Avatar upload ──
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `avatars/${user.id}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      setAvatarUrl(urlData?.publicUrl || null);
    } catch (err) {
      console.warn('avatar upload:', err);
    } finally {
      setUploading(false);
    }
  };

  const initial = (prenom || user?.email || 'U').trim().charAt(0).toUpperCase();

  const handleSignOut = async () => {
    if (onSignOut) await onSignOut();
    if (onGoHome) onGoHome();
  };

  const handleClose = () => {
    if (onClose) onClose();
    else if (onGoHome) onGoHome();
  };

  // ── Résiliation d'abonnement ───────────────────────────────
  // Flow (livré 2026-05-31, branchement Stripe) :
  //   1. Confirm modal d'explication (fin de période préservée).
  //   2. POST /api/billing/cancel-subscription (Bearer JWT) → backend
  //      pose cancel_at_period_end=true sur l'abo Stripe.
  //   3. Au succès, modale de confirmation avec la date de fin.
  //
  // Fallback mailto : si l'API renvoie `reason: 'missing_sub_id'` (cas
  // d'un abo créé avant le fix webhook 2026-05-27 — stripe_subscription_id
  // jamais peuplé) OU si le réseau/backend pète, on retombe sur l'ancien
  // mailto pour que David puisse traiter à la main dans Stripe Dashboard.
  // Pas de régression possible.
  const handleCancelSubscription = async () => {
    const ok = await confirmDialog({
      title: s.reglages.cancelSubModalTitle,
      message: s.reglages.cancelSubModalMessage,
      confirmLabel: s.reglages.cancelSubModalConfirm,
      cancelLabel: s.common.cancel,
    });
    if (ok !== 'confirm') return;

    const fallbackToMailto = () => {
      const subject = s.reglages.mailtoCancelSubSubject;
      const body = (s.reglages.mailtoCancelSubBody || '')
        .replace('{email}', user?.email || '');
      if (typeof window !== 'undefined') {
        window.location.href = buildMailto(subject, body);
      }
    };

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('no_session');

      const res = await fetch(`${API}/api/billing/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const payload = await res.json().catch(() => ({}));

      // Cas "OK" : l'abo Stripe est marqué cancel_at_period_end OU
      // l'était déjà. On affiche la date de fin si on l'a (timestamp
      // Unix en secondes côté Stripe).
      if (res.ok && payload?.ok) {
        const cancelAt = payload.cancelAt;
        const dateLabel = cancelAt
          ? new Date(cancelAt * 1000).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', {
              day: 'numeric', month: 'long', year: 'numeric',
            })
          : null;
        const msgTemplate = s.reglages.cancelSubSuccessMessage
          || 'Your subscription is cancelled. You keep access until {date}.';
        const message = dateLabel
          ? msgTemplate.replace('{date}', dateLabel)
          : (s.reglages.cancelSubSuccessMessageNoDate || msgTemplate.replace('{date}', '—'));
        await confirmDialog({
          title: s.reglages.cancelSubSuccessTitle || 'Résiliation enregistrée',
          message,
          confirmLabel: s.common.ok,
          cancelLabel: '',
        });
        // Refresh des crédits pour que le hint "Renouvelle le …" disparaisse
        // au prochain ouverture de la modale (l'abo est encore actif mais
        // le user a vu la confirmation).
        if (onClose) onClose();
        return;
      }

      // Cas "abo sans sub_id" (Sébastien) ou autre erreur connue côté
      // backend → fallback mailto sans message d'erreur agressif.
      if (payload?.reason === 'missing_sub_id') {
        fallbackToMailto();
        return;
      }

      // Erreur inconnue : on log + fallback mailto + message neutre.
      console.warn('[reglages] cancel-subscription returned non-ok:', payload);
      fallbackToMailto();
    } catch (e) {
      console.warn('[reglages] cancel-subscription threw:', e?.message || e);
      fallbackToMailto();
    }
  };

  // ── Suppression de compte ──────────────────────────────────
  // Flow en 2 étapes (refonte 2026-05-06bis pour sécurité contre les
  // suppressions accidentelles + hijack de session) :
  //   1. Le user clique "Supprimer mon compte" → on POST vers
  //      /api/account/request-deletion (Bearer JWT). Le backend génère
  //      un token signé HMAC 1h et envoie un mail branded au user avec
  //      un lien `versions.studio/confirm-delete-account?token=...`.
  //   2. Le user clique le lien dans le mail → écran
  //      ConfirmDeleteAccountScreen → POST /api/account/confirm-deletion
  //      avec le token → le backend appelle la RPC service-role
  //      `delete_user_account(p_user_id)` qui purge en cascade toutes
  //      les tables et auth.users.
  //
  // La notif ops à David part automatiquement via le webhook Supabase
  // auth.users DELETE (cf. versions-api/api/_internal.js).
  //
  // Storage : les fichiers (audio, avatars, project-covers, track-covers)
  // ne sont pas purgés par la RPC — ils deviennent orphelins et seront
  // nettoyés via un job batch ultérieur si besoin.
  const handleDeleteAccount = async () => {
    const ok = await confirmDialog({
      title: s.reglages.deleteAccountModalTitle,
      message: s.reglages.deleteAccountModalMessage,
      confirmLabel: s.reglages.deleteAccountModalConfirm,
      cancelLabel: s.common.cancel,
      danger: true,
    });
    if (ok !== 'confirm') return;
    try {
      // On a besoin du JWT du user pour authentifier le request côté backend.
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('no_session');
      const res = await fetch(`${API}/api/account/request-deletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`request_failed_${res.status}`);
      // Mail envoyé. On affiche une modale d'info (alert mode, pas un
      // confirm) pour rappeler au user d'aller cliquer le lien dans son
      // email. La suppression effective se fera après ce clic via
      // l'écran /confirm-delete-account. On NE déconnecte PAS l'user ici
      // — il reste connecté tant qu'il n'a pas confirmé.
      await confirmDialog({
        title: s.reglages.deleteAccountEmailSentTitle,
        message: s.reglages.deleteAccountEmailSentMessage,
        confirmLabel: s.common.ok,
        cancelLabel: '',
      });
      if (onClose) onClose();
    } catch (e) {
      console.warn('[reglages] request-deletion failed:', e?.message || e);
      await confirmDialog({
        title: s.reglages.deleteAccountErrorTitle,
        message: s.reglages.deleteAccountErrorMessage,
        confirmLabel: s.common.ok,
        cancelLabel: '',
        danger: true,
      });
    }
  };

  // Tant que la facturation n'est pas branchée, David est affiché Premium manuellement.
  // À remplacer par user?.plan === 'premium' quand les abonnements seront actifs.
  const planLabel = s.reglages.miniAccountPlanPremium;

  return (
    <div className="rg-mini">
      <div className="rg-mm-title">
        {s.reglages.miniTitleBefore} <em>{s.reglages.miniTitleEm}</em>
      </div>

      {loadingProfile ? (
        <div className="rg-loading">{s.common.loading}</div>
      ) : (
        <>
          {/* Section Photo de profil retirée (refonte modale 2026-04-30bis) :
              elle alourdissait la modale sans usage clair (pas d'affichage
              de l'avatar dans l'app au-delà de la sidebar). On garde le
              code de upload côté logique au cas où on la réintroduit, mais
              plus de UI ici. */}

          {/* ── Nom complet ── */}
          <div className="rg-row is-stack">
            <div>
              <div className="rg-label">{s.reglages.miniProfileLabel}</div>
              {s.reglages.miniProfileHint ? (
                <div className="rg-hint">{s.reglages.miniProfileHint}</div>
              ) : null}
            </div>
            <div className="rg-inputs">
              <input
                className="rg-input"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                placeholder={s.reglages.firstNamePlaceholder}
              />
              <input
                className="rg-input"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder={s.reglages.lastNamePlaceholder}
              />
            </div>
          </div>

          {/* ── Langue ── */}
          <div className="rg-row">
            <div>
              <div className="rg-label">{s.reglages.miniLangLabel}</div>
              {s.reglages.miniLangHint ? (
                <div className="rg-hint">{s.reglages.miniLangHint}</div>
              ) : null}
            </div>
            <div className="rg-toggle" role="group" aria-label={s.reglages.miniLangLabel}>
              <button
                type="button"
                className={lang === 'fr' ? 'on' : ''}
                onClick={() => setLang('fr')}
              >
                FR
              </button>
              <button
                type="button"
                className={lang === 'en' ? 'on' : ''}
                onClick={() => setLang('en')}
              >
                EN
              </button>
            </div>
          </div>

          {/* ── DAW par défaut ── */}
          <div className="rg-row">
            <div>
              <div className="rg-label">{s.reglages.miniDawLabel}</div>
              <div className="rg-hint">{s.reglages.miniDawHint}</div>
            </div>
            <div className="rg-select-wrap">
              <select
                className="rg-select"
                value={defaultDaw}
                onChange={(e) => setDefaultDaw(e.target.value)}
              >
                <option value="">{s.reglages.dawNone}</option>
                {DAWS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <div className="rg-select-arrow">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M2 4 L6 8 L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>

          {/* Section Coordonnées bancaires retirée (refonte modale 2026-04-30bis) :
              les achats passent par /pricing → Stripe Checkout, pas par
              une saisie IBAN directe dans l'app. Les states iban/bic
              côté hook restent en mémoire pour ne pas casser un éventuel
              ré-attach futur, mais plus de UI. */}

          {/* ── Compte ── crédits restants + abonnement actif si applicable.
              CTA "+ Acheter des crédits" pill amber posé à côté pour
              que le user puisse top-up directement depuis la modale.
              Click → navigue vers /pricing + ferme la modale (via
              onClose). */}
          {(() => {
            const balance = creditsRow?.balance_remaining;
            const monthly = Number(creditsRow?.monthly_grant || 0);
            const isSubscribed = monthly > 0;
            const credText = balance != null
              ? `${balance} ${balance > 1 ? (s.reglages.creditsPlural || 'crédits') : (s.reglages.creditSingular || 'crédit')}`
              : null;
            const planText = isSubscribed
              ? (s.reglages.miniAccountPlanPremium || 'Premium')
              : (s.reglages.miniAccountPlanFree || 'Free');
            const renewMs = creditsRow?.monthly_renews_at
              ? new Date(creditsRow.monthly_renews_at).getTime()
              : null;
            const renewText = (isSubscribed && Number.isFinite(renewMs))
              ? new Date(renewMs).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', { day: 'numeric', month: 'short' })
              : null;
            const goToPricing = () => {
              // Navigation via History API + dispatch d'un popstate pour que
              // le routeur d'App.jsx se réaligne sur la nouvelle URL.
              if (typeof window !== 'undefined') {
                window.history.pushState({ screen: 'pricing' }, '', '/pricing');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
              if (onClose) onClose();
              else if (onGoHome) onGoHome();
            };
            return (
              <>
                <div className="rg-row is-stack">
                  <div className="rg-account-row">
                    <div>
                      <div className="rg-label">{s.reglages.miniAccountLabel}</div>
                      <div className="rg-hint">{user?.email || ''}</div>
                    </div>
                    <div className="rg-account-meta">
                      {credText && (
                        <span className="rg-account-credits">{credText}</span>
                      )}
                      <span className={`rg-account-plan${isSubscribed ? ' is-premium' : ''}`}>
                        {planText}
                      </span>
                      {renewText && (
                        <span className="rg-account-renew">
                          {(s.reglages.renewsOn || 'Renouvelle le {date}').replace('{date}', renewText)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rg-account-cta"
                    onClick={goToPricing}
                  >
                    <span className="rg-account-cta-icon" aria-hidden="true">+</span>
                    <span>{s.reglages.buyCreditsCta || (isSubscribed ? 'Gérer mon abonnement' : 'Acheter des crédits')}</span>
                  </button>
                </div>

                {/* Résilier l'abonnement — visible seulement si l'user est
                    abonné. Modale d'explication + mailto contact pré-rempli. */}
                {isSubscribed && (
                  <div className="rg-row">
                    <div>
                      <div className="rg-label">{s.reglages.cancelSubLabel}</div>
                      <div className="rg-hint">{s.reglages.cancelSubHint}</div>
                    </div>
                    <button
                      type="button"
                      className="rg-btn"
                      onClick={handleCancelSubscription}
                    >
                      {s.reglages.cancelSubBtn}
                    </button>
                  </div>
                )}
              </>
            );
          })()}

          {/* ── Nouvelles d'Archipel Audio ── enregistré immédiatement
              (comme la langue), pas via le bouton Enregistrer. */}
          {archipelOn !== null && (
            <div className="rg-row">
              <div>
                <div className="rg-label">{s.reglages.archipelLabel}</div>
                <div className="rg-hint">{s.reglages.archipelHint}</div>
                {archipelErr && (
                  <div className="rg-hint" style={{ color: 'var(--red)' }}>{s.reglages.archipelError}</div>
                )}
              </div>
              <div className="rg-toggle" role="group" aria-label={s.reglages.archipelLabel}>
                <button
                  type="button"
                  className={archipelOn ? 'on' : ''}
                  aria-pressed={archipelOn}
                  disabled={archipelBusy}
                  onClick={() => handleArchipel(true)}
                >
                  {s.reglages.archipelYes}
                </button>
                <button
                  type="button"
                  className={!archipelOn ? 'on' : ''}
                  aria-pressed={!archipelOn}
                  disabled={archipelBusy}
                  onClick={() => handleArchipel(false)}
                >
                  {s.reglages.archipelNo}
                </button>
              </div>
            </div>
          )}

          {/* ── Revoir le guide d'utilisation ──
              Efface les flags localStorage des DEUX guides (home + fiche)
              et déclenche un événement custom écouté par chaque instance
              de OnboardingHints. La modale Réglages se ferme et le guide
              de l'écran courant réapparaît immédiatement. */}
          <div className="rg-row">
            <div>
              <div className="rg-label">{s.reglages.replayOnboardingLabel}</div>
              <div className="rg-hint">{s.reglages.replayOnboardingHint}</div>
            </div>
            <button
              type="button"
              className="rg-btn"
              onClick={() => {
                try {
                  // Compat avec l'ancienne clef + les deux nouvelles clefs scoped
                  ['versions_onboarding_done',
                   'versions_onboarding_done_home',
                   'versions_onboarding_done_fiche'].forEach((k) => {
                    window.localStorage.removeItem(k);
                  });
                } catch {}
                try { window.dispatchEvent(new CustomEvent('versions:replay-onboarding')); } catch {}
                if (onClose) onClose();
                else if (onGoHome) onGoHome();
              }}
            >
              {s.reglages.replayOnboardingBtn}
            </button>
          </div>

          {/* ── Supprimer mon compte ── danger zone, en bas avant le footer.
              Tant que la RPC backend n'existe pas, la modale demande à
              l'utilisateur d'écrire au support — bouton mailto pré-rempli. */}
          <div className="rg-row rg-row-danger">
            <div>
              <div className="rg-label">{s.reglages.deleteAccountLabel}</div>
              <div className="rg-hint">{s.reglages.deleteAccountHint}</div>
            </div>
            <button
              type="button"
              className="rg-btn is-danger"
              onClick={handleDeleteAccount}
            >
              {s.reglages.deleteAccountBtn}
            </button>
          </div>

          {saved && <div className="rg-saved-chip">{s.reglages.saved}</div>}

          {/* ── Footer ── */}
          <div className="rg-foot">
            <button
              type="button"
              className="rg-btn is-danger"
              onClick={handleSignOut}
            >
              {s.reglages.signOut}
            </button>
            <button
              type="button"
              className="rg-btn is-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? s.reglages.saving : s.reglages.save}
            </button>
            <button
              type="button"
              className="rg-btn"
              onClick={handleClose}
            >
              {s.reglages.close}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
